/* eslint-disable no-underscore-dangle */
/* eslint-disable camelcase */
import React, { useState, useContext, ReactNode } from 'react';
import { withRouter, RouteComponentProps, Link, useRouteMatch } from 'react-router-dom';
import { Input, DatePicker, message, Row, Col, Select, Modal, Popover } from 'antd';
import { SearchOutlined, FileExcelOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { v4 as uuidv4 } from 'uuid';
import { useQuery } from '@apollo/react-hooks';
import { formatDateTime } from 'shared/utils/dateTime';
import formatCurrency from 'shared/utils/formatCurrency';
import baseUrl from 'shared/config/baseUrl';
import CODS_QUERY, { CodsData, CodsVars } from 'shared/graphql/query/newCods';
import {
  CodContext,
  SelectedCodDetailType,
} from 'views/CashOnDelivery/Routes/CashOnDeliveryByShipment/common/context/CodContext';
import { Text, Button, SelectVendorsField, TableSearch } from 'shared/components';
import { CodStatus } from 'views/CashOnDelivery/Routes/CashOnDeliveryByShipment/common/model/codStatus.model';
import { ToolsContainer } from './Styles';

const { Option } = Select;
const { RangePicker } = DatePicker;
const { confirm } = Modal;

interface CodListTableProps extends RouteComponentProps {
  status: CodStatus;
  pageSize: number;
  onChangePageSize: (pageSize: number) => void;
  currentPage: number;
  onChangeCurrentPage: (currentPage: number) => void;
}

type CodListDataTable = {
  key: number;
  tools: ReactNode;
  column1: string;
  column2: string;
  column3: ReactNode;
  column4: ReactNode;
  column5: string;
  column6: string;
  column7: string;
  column8: string;
};

const CodListTable: React.FC<CodListTableProps> = (props) => {
  const { status, pageSize, onChangePageSize, currentPage, onChangeCurrentPage } = props;
  const [vendorId, setVendorId] = useState<string>('');
  const [codInNumber, setCodInNumber] = useState<string>('');
  const [shipmentNumber, setShipmentNumber] = useState<string>('');
  const [dueDate, setDueDate] = useState<string>('');
  const [orderBy, setOrderBy] = useState<string>('');
  const [orderType, setOrderType] = useState<'1' | '-1' | ''>('');
  const { path } = useRouteMatch();
  const { setSelectedCodDetail } = useContext(CodContext);

  const { loading, error, data } = useQuery<CodsData, CodsVars>(CODS_QUERY, {
    variables: {
      cod_status: status,
      vendor: vendorId,
      cod_in_number: codInNumber,
      shipment_numbers: shipmentNumber,
      due_date: dueDate,
      order_by: orderBy,
      order_type: orderType,
      page: currentPage,
      showData: pageSize,
    },
    fetchPolicy: 'cache-and-network',
  });

  const showConfirmDownloadFile = (codInId: string) => {
    const modal = {
      title: 'ยืนยัน',
      icon: <ExclamationCircleOutlined />,
      content: (
        <>
          ต้องการ <Text strong>ดาวน์โหลดไฟล์</Text> ใช่หรือไม่?
        </>
      ),
      okText: 'ดาวน์โหลด',
      onOk() {
        window.location.href = `${baseUrl}/report-NewCOD-01/${codInId}`;
      },
      cancelText: 'ยกเลิก',
    };

    confirm(modal);
  };

  const contentPopOverShipmentNumber = (value: string[]) => {
    return (
      <>
        <Row>
          <Col>
            <Text>Shipment No: </Text>
          </Col>
        </Row>
        {value.map((item) => (
          <Row key={uuidv4()}>
            <Col>
              <Text strong>{item}</Text>
            </Col>
          </Row>
        ))}
      </>
    );
  };

  const popOverShipmentNumber = (value: string[]) => {
    if (value.length === 1) {
      return <Text>{value[0]}</Text>;
    }
    if (value.length > 1) {
      return (
        <Popover placement="rightTop" content={contentPopOverShipmentNumber(value)}>
          <div style={{ display: 'inline' }}>
            <Text>{value.length} รหัส</Text>
          </div>
        </Popover>
      );
    }
    return '-';
  };

  const columns = [
    {
      title: 'เครื่องมือ',
      dataIndex: 'tools',
      width: 80,
    },
    {
      title: 'ชื่อ 3PL',
      dataIndex: 'column1',
      width: 300,
      search: <SelectVendorsField onChange={(value: string) => setVendorId(value)} />,
    },
    {
      title: 'รหัส COD-IN',
      dataIndex: 'column2',
      sortName: 'cod_in_number',
      width: 150,
      search: (
        <Input
          allowClear
          value={codInNumber}
          placeholder="ค้นหา"
          onChange={(event) => setCodInNumber(event.target.value)}
        />
      ),
    },
    {
      title: 'ยอดรวม COD (บาท)',
      dataIndex: 'column3',
      sortName: 'total_cod',
      width: 150,
      align: 'right',
    },
    {
      title: 'Shipment Number',
      dataIndex: 'column4',
      width: 150,
      search: (
        <Input
          allowClear
          value={shipmentNumber}
          placeholder="ค้นหา"
          onChange={(event) => setShipmentNumber(event.target.value)}
        />
      ),
    },
    {
      title: 'วันที่ต้องทำจ่าย',
      dataIndex: 'column5',
      width: 150,
      search: (
        <RangePicker
          onChange={(values) => {
            const formatRangDate =
              values === null
                ? ''
                : `${values[0]!.format('YYYY-MM-DD')}:${values[1]!.format('YYYY-MM-DD')}`;

            setDueDate(formatRangDate);
          }}
          format="YYYY/MM/DD"
          placeholder={['เริ่มต้น', 'สิ้นสุด']}
        />
      ),
    },
    {
      title: 'วันที่แจ้งโอน',
      dataIndex: 'column6',
      width: 150,
    },
    {
      title: 'ผู้ตรวจสอบ',
      dataIndex: 'column7',
      width: 150,
    },
    {
      title: 'อัพเดทล่าสุด',
      dataIndex: 'column8',
      sortName: 'last_update',
      width: 150,
    },
  ];

  let codListData: CodListDataTable[] = [];
  let totalDocument = 0;

  if (data?.newCods) {
    const { cods } = data.newCods;
    codListData = cods.map(
      (item, index): CodListDataTable => ({
        key: index,
        tools: (
          <ToolsContainer>
            <Link to={`${path}/detail/${item._id}`}>
              <Button
                onClick={() => {
                  setSelectedCodDetail(
                    (prevState): SelectedCodDetailType => ({
                      ...prevState,
                      codId: item._id,
                      codStatus: item.cod_status,
                      codNumber: item.cod_in_number,
                    }),
                  );
                }}
                color="grey--text-only"
                icon={<SearchOutlined />}
                size="small"
              />
            </Link>
            {(status === CodStatus.OPS_PENDING || status === CodStatus.PENDING) && (
              <Button
                onClick={() => showConfirmDownloadFile(item._id)}
                color="grey--text-only"
                icon={<FileExcelOutlined />}
                size="small"
              />
            )}
          </ToolsContainer>
        ),
        column1: item.vendor?.full_name || '-',
        column2: item.cod_in_number || '-',
        column3: (
          <Text strong customType="primary">
            {formatCurrency(item.total_cod)}
          </Text>
        ),
        column4: popOverShipmentNumber(item.shipment_numbers),
        column5: item.due_date ? formatDateTime(item.due_date) : '-',
        column6: item.cod_in_date ? formatDateTime(item.cod_in_date) : '-',
        column7: item.ops_employee?.full_name || '-',
        column8: item.last_update ? formatDateTime(item.last_update) : '-',
      }),
    );
    totalDocument = data.newCods.totalDocument;
  }

  if (error) {
    message.error(error.message);
  }

  return (
    <>
      <Row
        align="middle"
        justify="space-between"
        style={{ borderBottom: '1px solid #e3e3e3', height: 60 }}
      >
        <Col>
          <Select defaultValue="all" style={{ width: 180 }}>
            <Option value="all">รายการทั้งหมด ({totalDocument})</Option>
          </Select>
        </Col>
        <Col>
          แสดง{' '}
          <Select
            onChange={(value) => {
              onChangeCurrentPage(1);
              onChangePageSize(value);
            }}
            value={pageSize}
            style={{ width: 80 }}
          >
            <Option value={10}>10</Option>
            <Option value={20}>20</Option>
            <Option value={30}>30</Option>
          </Select>
        </Col>
      </Row>

      <TableSearch
        columns={columns}
        dataSource={codListData}
        loading={loading}
        pagination={{
          current: currentPage,
          pageSize,
          total: totalDocument,
          showSizeChanger: false,
          onChange: (value: number) => onChangeCurrentPage(value),
        }}
        onSort={(sort) => {
          setOrderBy(sort.orderBy);
          setOrderType(sort.orderType);
        }}
      />
    </>
  );
};

export default withRouter(CodListTable);
